import type { PalletRow, PalletLineRow, PalletViewRow } from '@/entities/consignments';
import type { ConsignmentLineItem } from '@/entities/consignments';

import type { LocalPallet } from './types';

/**
 * Parses a string weight/volume value to a number.
 * Returns 0 for null, undefined, empty string, or non-parseable values.
 *
 * Values can be display-formatted (US locale) with a unit suffix, e.g.
 * "1,234.5 kg" — thousands separators are stripped so parseFloat does not stop
 * at the first comma.
 */
export function parseNumericValue(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = parseFloat(value.replace(/,/g, ''));
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Computes aggregate totals for a set of consignment lines.
 * Sums parsed weight and volume values, counts lines.
 */
export function computePalletTotals(lines: ConsignmentLineItem[]): {
  totalWeight: number;
  totalVolume: number;
  lineCount: number;
} {
  let totalWeight = 0;
  let totalVolume = 0;

  for (const line of lines) {
    totalWeight += parseNumericValue(line.weight);
    totalVolume += parseNumericValue(line.volume);
  }

  return {
    totalWeight,
    totalVolume,
    lineCount: lines.length
  };
}

/**
 * Transforms consignment lines into a unified hierarchical list:
 *
 * - Lines NOT assigned to any pallet → top-level rows (level 0)
 * - Pallets → expandable parent rows (level 0) with assigned lines as children (level 1)
 *
 * Pallet membership is taken from each pallet's `assignedLineIds` (built by
 * `derivePalletsFromLines` from the line `palletIds`/`palletNames` arrays). A
 * line may belong to several pallets, in which case it appears as a child row
 * under each of them.
 *
 * Ordering:
 * 1. Unassigned lines first, ordered by index ascending
 * 2. Pallet groups after, sorted by palletNumber ascending (numeric-aware)
 *    - Child lines within each pallet sorted by index ascending
 */
export function transformToHierarchicalRows(lines: ConsignmentLineItem[], pallets: LocalPallet[]): PalletViewRow[] {
  const lineById = new Map(lines.map((line) => [line.id, line]));
  const assignedLineIds = new Set<string>();
  for (const pallet of pallets) {
    for (const lineId of pallet.assignedLineIds) {
      assignedLineIds.add(lineId);
    }
  }

  const rows: PalletViewRow[] = [];

  // 1. Unassigned lines as top-level rows
  const unassignedLines = lines.filter((line) => !assignedLineIds.has(line.id)).sort((a, b) => a.index - b.index);
  for (const line of unassignedLines) {
    rows.push({
      ...line,
      level: 0
    } as PalletLineRow & { level: 0 });
  }

  // 2. Pallet groups, sorted by palletNumber ascending (numeric-aware)
  const sortedPallets = [...pallets].sort((a, b) =>
    a.palletNumber.localeCompare(b.palletNumber, undefined, { numeric: true })
  );

  for (const pallet of sortedPallets) {
    const assignedLines = pallet.assignedLineIds
      .map((lineId) => lineById.get(lineId))
      .filter((line): line is ConsignmentLineItem => line !== undefined)
      .sort((a, b) => a.index - b.index);

    const childRows: PalletLineRow[] = assignedLines.map((line) => ({
      ...line,
      level: 1 as const,
      parentId: pallet.id
    }));

    const { totalWeight, totalVolume, lineCount } = computePalletTotals(assignedLines);

    const palletRow: PalletRow = {
      id: pallet.id,
      palletNumber: pallet.palletNumber,
      totalWeight: `${totalWeight.toFixed(2)} kg`,
      totalVolume: `${totalVolume.toFixed(4)} m³`,
      lineCount,
      level: 0,
      children: childRows
    };

    rows.push(palletRow);
    // Flatten children into the data array so HierarchicalTableBody can render them
    rows.push(...childRows);
  }

  return rows;
}
